import { DollarOutlined, QuestionCircleOutlined, CalculatorOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, message, Modal, Space, Card, Descriptions, Tour, ConfigProvider, Switch } from 'antd';
import type { TourProps } from 'antd';
import React, { useEffect, useState, useRef } from 'react';
import { shippingConfigService } from '../services/shippingConfigService';
import { storeService } from '../services/storeService';
import type { ShippingConfig } from '../types/shipping';
import type { Store } from '../types/store';
import AdminLayout from '../components/AdminLayout';
import viVN from 'antd/locale/vi_VN';

const ShippingManagement: React.FC = () => {
    const [config, setConfig] = useState<ShippingConfig | null>(null);
    const [stores, setStores] = useState<Store[]>([]);
    const [loading, setLoading] = useState(false);
    const [shippingModalVisible, setShippingModalVisible] = useState(false);
    const [calculateModalVisible, setCalculateModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [calculateForm] = Form.useForm();
    const [calculatedFee, setCalculatedFee] = useState<any>(null);
    const [calculating, setCalculating] = useState(false);
    const [updatingShipping, setUpdatingShipping] = useState(false);
    const [isTourOpen, setIsTourOpen] = useState(false);

    // Tour refs
    const titleRef = useRef(null);
    const shippingCardRef = useRef(null);

    // Tour steps
    const tourSteps: TourProps['steps'] = [
        {
            title: 'Quản lý phí vận chuyển',
            description: 'Chào mừng bạn đến với trang Quản lý phí vận chuyển! Đây là nơi bạn có thể thiết lập các chính sách phí vận chuyển cho khách hàng.',
            target: () => titleRef.current,
        },
        {
            title: 'Cấu hình phí vận chuyển',
            description: 'Phần này cho phép bạn thiết lập các chính sách phí vận chuyển như miễn phí vận chuyển, miễn phí theo khoảng cách, phụ phí và giảm phí. Các cài đặt này sẽ ảnh hưởng đến việc tính toán phí vận chuyển cho đơn hàng.',
            target: () => shippingCardRef.current,
        },
        {
            title: 'Kết thúc hướng dẫn!',
            description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn có thể quản lý hiệu quả các cài đặt phí vận chuyển để đảm bảo trải nghiệm tốt nhất cho khách hàng.',
            target: () => titleRef.current,
        },
    ];

    useEffect(() => {
        fetchConfig();
        fetchStores();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const data = await shippingConfigService.getConfig();
            setConfig(data);
        } catch (error) {
            console.error('Error fetching shipping config:', error);
            message.error('Không thể tải cấu hình vận chuyển');
        }
        setLoading(false);
    };

    const fetchStores = async () => {
        try {
            const data = await storeService.getActiveStores();
            setStores(data);
        } catch (error) {
            console.error('Error fetching stores:', error);
            message.error('Không thể tải danh sách cửa hàng');
        }
    };

    const handleShippingSubmit = async (values: any) => {
        setUpdatingShipping(true);
        try {
            const formattedValues = {
                maxFee: values.maxFee || 0,
                enableMaxFee: values.enableMaxFee || false,
                isThresholdDistance: values.isThresholdDistance || false,
                thresholdDistance: values.thresholdDistance || 0,
                feePerKm: values.feePerKm || 0,
                enableSurcharge: values.enableSurcharge || false,
                surchargeAmount: values.surchargeAmount || 0,
                enableFeeDiscount: values.enableFeeDiscount || false,
                feeDiscountAmount: values.feeDiscountAmount || 0,
                enableFreeShipping: values.enableFreeShipping || false,
                enableDistanceBasedFreeShipping: values.enableDistanceBasedFreeShipping || false,
                freeShippingDistanceThreshold: values.freeShippingDistanceThreshold || 0,
                updatedAt: new Date().toISOString(),
            };

            const cleanedValues = Object.fromEntries(
                Object.entries(formattedValues).filter(([_, value]) => value !== undefined && value !== null)
            );

            await shippingConfigService.updateConfig(cleanedValues);
            message.success('Cập nhật cấu hình vận chuyển thành công');
            setShippingModalVisible(false);
            form.resetFields();
            await fetchConfig();
        } catch (error) {
            console.error('Error saving shipping config:', error);
            message.error('Có lỗi xảy ra khi lưu cấu hình');
        } finally {
            setUpdatingShipping(false);
        }
    };

    const handleCalculateSubmit = async (values: any) => {
        setCalculating(true);
        try {
            const params = new URLSearchParams({
                pick_province: values.pick_province,
                pick_district: values.pick_district,
                province: values.province,
                district: values.district,
                address: values.address || '',
                weight: values.weight.toString(),
                value: (values.value || 0).toString(),
                deliver_option: values.deliver_option || "none",
            });

            const response = await fetch(`https://services.giaohangtietkiem.vn/services/shipment/fee?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Token': values.token,
                },
            });

            const data = await response.json();
            if (data.success) {
                setCalculatedFee(data.fee);
                message.success('Tính phí vận chuyển thành công');
            } else {
                message.error(data.message || 'Có lỗi xảy ra khi tính phí vận chuyển');
            }
        } catch (error) {
            console.error('Error calculating shipping fee:', error);
            message.error('Có lỗi xảy ra khi tính phí vận chuyển');
        }
        setCalculating(false);
    };

    const ShippingForm: React.FC = () => {
        const [enableSurcharge, setEnableSurcharge] = useState(config?.enableSurcharge ?? false);
        const [enableFeeDiscount, setEnableFeeDiscount] = useState(config?.enableFeeDiscount ?? false);
        const [enableFreeShipping, setEnableFreeShipping] = useState(config?.enableFreeShipping ?? false);
        const [enableDistanceBasedFreeShipping, setEnableDistanceBasedFreeShipping] = useState(config?.enableDistanceBasedFreeShipping ?? false);

        return (
            <Form
                form={form}
                layout="vertical"
                onFinish={handleShippingSubmit}
                initialValues={config || {}}
            >
                <Space align="baseline" style={{ marginBottom: 16, width: '100%' }}>
                    <Form.Item
                        name="enableFreeShipping"
                        label="Miễn phí vận chuyển"
                        valuePropName="checked"
                    >
                        <Switch
                            checked={enableFreeShipping}
                            onChange={(checked) => {
                                setEnableFreeShipping(checked);
                                form.setFieldsValue({ enableFreeShipping: checked });
                            }}
                        />
                    </Form.Item>
                </Space>

                <Space align="baseline" style={{ marginBottom: 16, width: '100%' }}>
                    <Form.Item
                        name="enableDistanceBasedFreeShipping"
                        label="Miễn phí vận chuyển theo khoảng cách"
                        valuePropName="checked"
                    >
                        <Switch
                            checked={enableDistanceBasedFreeShipping}
                            onChange={(checked) => {
                                setEnableDistanceBasedFreeShipping(checked);
                                form.setFieldsValue({ enableDistanceBasedFreeShipping: checked });
                                if (!checked) {
                                    form.setFieldsValue({ freeShippingDistanceThreshold: undefined });
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="freeShippingDistanceThreshold"
                        label="Khoảng cách miễn phí (km)"
                        rules={[{ required: enableDistanceBasedFreeShipping, message: 'Vui lòng nhập khoảng cách miễn phí' }]}
                    >
                        <InputNumber
                            min={0}
                            step={0.1}
                            style={{ width: '100%' }}
                            placeholder="Ví dụ: 5"
                            disabled={!enableDistanceBasedFreeShipping}
                        />
                    </Form.Item>
                </Space>

                <Space align="baseline" style={{ marginBottom: 16, width: '100%' }}>
                    <Form.Item
                        name="enableSurcharge"
                        label="Áp dụng phụ phí"
                        valuePropName="checked"
                    >
                        <Switch
                            checked={enableSurcharge}
                            onChange={(checked) => {
                                setEnableSurcharge(checked);
                                form.setFieldsValue({ enableSurcharge: checked });
                                if (!checked) {
                                    form.setFieldsValue({ surchargeAmount: undefined });
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="surchargeAmount"
                        label="Phụ phí (VNĐ)"
                        rules={[{ required: enableSurcharge, message: 'Vui lòng nhập phụ phí' }]}
                    >
                        <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            disabled={!enableSurcharge}
                        />
                    </Form.Item>
                </Space>

                <Space align="baseline" style={{ marginBottom: 16, width: '100%' }}>
                    <Form.Item
                        name="enableFeeDiscount"
                        label="Áp dụng giảm phí"
                        valuePropName="checked"
                    >
                        <Switch
                            checked={enableFeeDiscount}
                            onChange={(checked) => {
                                setEnableFeeDiscount(checked);
                                form.setFieldsValue({ enableFeeDiscount: checked });
                                if (!checked) {
                                    form.setFieldsValue({ feeDiscountAmount: undefined });
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="feeDiscountAmount"
                        label="Giảm phí (VNĐ)"
                        rules={[{ required: enableFeeDiscount, message: 'Vui lòng nhập số tiền giảm phí' }]}
                    >
                        <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            disabled={!enableFeeDiscount}
                        />
                    </Form.Item>
                </Space>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={updatingShipping}>
                        Cập nhật cấu hình
                    </Button>
                </Form.Item>
            </Form>
        );
    };

    const CalculateForm: React.FC = () => {
        return (
            <Form
                form={calculateForm}
                layout="vertical"
                onFinish={handleCalculateSubmit}
            >
                <Form.Item
                    name="token"
                    label="API Token"
                    rules={[{ required: true, message: 'Vui lòng nhập API Token' }]}
                >
                    <Input.Password placeholder="Nhập API Token của GHTK" />
                </Form.Item>

                <Form.Item
                    name="pick_province"
                    label="Tỉnh/Thành phố lấy hàng"
                    rules={[{ required: true, message: 'Vui lòng nhập tỉnh/thành phố lấy hàng' }]}
                >
                    <Input placeholder="Ví dụ: Hà Nội" />
                </Form.Item>

                <Form.Item
                    name="pick_district"
                    label="Quận/Huyện lấy hàng"
                    rules={[{ required: true, message: 'Vui lòng nhập quận/huyện lấy hàng' }]}
                >
                    <Input placeholder="Ví dụ: Quận Hai Bà Trưng" />
                </Form.Item>

                <Form.Item
                    name="province"
                    label="Tỉnh/Thành phố nhận hàng"
                    rules={[{ required: true, message: 'Vui lòng nhập tỉnh/thành phố nhận hàng' }]}
                >
                    <Input placeholder="Ví dụ: Hà Nội" />
                </Form.Item>

                <Form.Item
                    name="district"
                    label="Quận/Huyện nhận hàng"
                    rules={[{ required: true, message: 'Vui lòng nhập quận/huyện nhận hàng' }]}
                >
                    <Input placeholder="Ví dụ: Quận Cầu Giấy" />
                </Form.Item>

                <Form.Item
                    name="address"
                    label="Địa chỉ nhận hàng"
                >
                    <Input.TextArea placeholder="Nhập địa chỉ chi tiết nhận hàng" />
                </Form.Item>

                <Form.Item
                    name="weight"
                    label="Khối lượng (gram)"
                    rules={[{ required: true, message: 'Vui lòng nhập khối lượng' }]}
                >
                    <InputNumber min={1} placeholder="Ví dụ: 1000" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    name="value"
                    label="Giá trị hàng hóa (VNĐ)"
                >
                    <InputNumber
                        min={0}
                        style={{ width: '100%' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    />
                </Form.Item>

                <Form.Item
                    name="deliver_option"
                    label="Phương thức vận chuyển"
                >
                    <Input placeholder="Nhập xteam hoặc none" />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={calculating}>
                        Tính phí vận chuyển
                    </Button>
                </Form.Item>

                {calculatedFee && (
                    <Card title="Kết quả tính phí">
                        <Descriptions column={1}>
                            <Descriptions.Item label="Gói cước">
                                {calculatedFee.name}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phí vận chuyển">
                                {calculatedFee.fee?.toLocaleString()} VNĐ
                            </Descriptions.Item>
                            <Descriptions.Item label="Phí bảo hiểm">
                                {calculatedFee.insurance_fee?.toLocaleString()} VNĐ
                            </Descriptions.Item>
                            <Descriptions.Item label="Hỗ trợ giao hàng">
                                {calculatedFee.delivery ? 'Có' : 'Không'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                )}
            </Form>
        );
    };

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý phí vận chuyển</h1>
                    <Space>
                        <Button
                            type="primary"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => setIsTourOpen(true)}
                        >
                            Hướng dẫn
                        </Button>
                    </Space>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* Phần cấu hình vận chuyển */}
                <Card
                    ref={shippingCardRef}
                    title={
                        <Space>
                            <DollarOutlined />
                            <span>Cấu hình phí vận chuyển</span>
                        </Space>
                    }
                    extra={
                        <Button
                            type="primary"
                            onClick={() => {
                                form.setFieldsValue(config);
                                setShippingModalVisible(true);
                            }}
                        >
                            Chỉnh sửa
                        </Button>
                    }
                    style={{ flex: 2 }}
                >
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        {/* Miễn phí vận chuyển */}
                        <Card
                            size="small"
                            title={
                                <Space>
                                    <DollarOutlined style={{ color: '#52c41a' }} />
                                    <span>Miễn phí vận chuyển</span>
                                </Space>
                            }
                            style={{
                                flex: 1,
                                minWidth: '250px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                    <span style={{ color: '#8c8c8c' }}>Trạng thái:</span>
                                    <span style={{
                                        marginLeft: '8px',
                                        color: config?.enableFreeShipping ? '#52c41a' : '#ff4d4f',
                                        fontWeight: 500
                                    }}>
                                        {config?.enableFreeShipping ? 'Đang áp dụng' : 'Không áp dụng'}
                                    </span>
                                </div>
                            </div>
                        </Card>

                        {/* Miễn phí vận chuyển theo khoảng cách */}
                        <Card
                            size="small"
                            title={
                                <Space>
                                    <EnvironmentOutlined style={{ color: '#1890ff' }} />
                                    <span>Miễn phí theo khoảng cách</span>
                                </Space>
                            }
                            style={{
                                flex: 1,
                                minWidth: '250px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                    <span style={{ color: '#8c8c8c' }}>Trạng thái:</span>
                                    <span style={{
                                        marginLeft: '8px',
                                        color: config?.enableDistanceBasedFreeShipping ? '#52c41a' : '#ff4d4f',
                                        fontWeight: 500
                                    }}>
                                        {config?.enableDistanceBasedFreeShipping ? 'Đang áp dụng' : 'Không áp dụng'}
                                    </span>
                                </div>
                                {config?.enableDistanceBasedFreeShipping && (
                                    <div>
                                        <span style={{ color: '#8c8c8c' }}>Khoảng cách:</span>
                                        <span style={{
                                            marginLeft: '8px',
                                            color: '#1890ff',
                                            fontWeight: 500
                                        }}>
                                            Dưới {config?.freeShippingDistanceThreshold} km
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Phụ phí */}
                        <Card
                            size="small"
                            title={
                                <Space>
                                    <span style={{ color: '#ff4d4f' }}>+</span>
                                    <span>Phụ phí</span>
                                </Space>
                            }
                            style={{
                                flex: 1,
                                minWidth: '250px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                    <span style={{ color: '#8c8c8c' }}>Trạng thái:</span>
                                    <span style={{
                                        marginLeft: '8px',
                                        color: config?.enableSurcharge ? '#52c41a' : '#ff4d4f',
                                        fontWeight: 500
                                    }}>
                                        {config?.enableSurcharge ? 'Đang áp dụng' : 'Không áp dụng'}
                                    </span>
                                </div>
                                {config?.enableSurcharge && (
                                    <div>
                                        <span style={{ color: '#8c8c8c' }}>Số tiền:</span>
                                        <span style={{
                                            marginLeft: '8px',
                                            color: '#1890ff',
                                            fontWeight: 500
                                        }}>
                                            {config?.surchargeAmount?.toLocaleString()} VNĐ
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Giảm phí */}
                        <Card
                            size="small"
                            title={
                                <Space>
                                    <span style={{ color: '#52c41a' }}>-</span>
                                    <span>Giảm phí</span>
                                </Space>
                            }
                            style={{
                                flex: 1,
                                minWidth: '250px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                    <span style={{ color: '#8c8c8c' }}>Trạng thái:</span>
                                    <span style={{
                                        marginLeft: '8px',
                                        color: config?.enableFeeDiscount ? '#52c41a' : '#ff4d4f',
                                        fontWeight: 500
                                    }}>
                                        {config?.enableFeeDiscount ? 'Đang áp dụng' : 'Không áp dụng'}
                                    </span>
                                </div>
                                {config?.enableFeeDiscount && (
                                    <div>
                                        <span style={{ color: '#8c8c8c' }}>Số tiền:</span>
                                        <span style={{
                                            marginLeft: '8px',
                                            color: '#1890ff',
                                            fontWeight: 500
                                        }}>
                                            {config?.feeDiscountAmount?.toLocaleString()} VNĐ
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>
                </Card>
            </div>

            {/* Modal chỉnh sửa cấu hình vận chuyển */}
            <Modal
                title="Chỉnh sửa cấu hình vận chuyển"
                visible={shippingModalVisible}
                onCancel={() => {
                    setShippingModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={800}
            >
                <ShippingForm />
            </Modal>

            {/* Modal tính phí vận chuyển GHTK */}
            <Modal
                title="Tính phí vận chuyển GHTK"
                visible={calculateModalVisible}
                onCancel={() => {
                    setCalculateModalVisible(false);
                    calculateForm.resetFields();
                    setCalculatedFee(null);
                }}
                footer={null}
                width={800}
            >
                <CalculateForm />
            </Modal>

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>
        </AdminLayout>
    );
};

export default ShippingManagement; 